import { useSiteMetadata } from "hooks";
import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";

interface AppData {
  SHOWFOOTER: string;
  USER_API_HOST: string;
}

const initialAppData: AppData = {
  SHOWFOOTER: "false",
  USER_API_HOST: "",
};

interface AppDataContextType {
  refresh: () => void;
  getAppData: () => AppData;
}

const AppDataContext = createContext<AppDataContextType>(
  {} as AppDataContextType
);

export const useAppData = (): AppDataContextType => {
  return useContext(AppDataContext);
};

type AppDataProviderProps = React.PropsWithChildren<{
  expireTime?: number; // Optional duration for auto-refresh
  children: React.ReactNode;
}>;

const AppDataProvider = ({ children }: AppDataProviderProps) => {
  const [currentAppData, setCurrentAppData] = useState<AppData>(initialAppData);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const { APPDATA_EXPIRE_SECONDS: expireTime } = useSiteMetadata();
  const [lastInitializedAt, setlastInitializedAt] = useState<number>(0);
  const fetchAppData = useCallback(
    // Call via refresh()
    async () =>
      await fetch("/cashify-data.json")
        .then((resp) => {
          if (resp.status && resp.status === 200) {
            return resp.json();
          } else {
            throw resp.statusText;
          }
        })
        .then((data: AppData) => {
          const newAppData: AppData = {
            SHOWFOOTER: data.SHOWFOOTER ?? initialAppData.SHOWFOOTER,
            USER_API_HOST: data.USER_API_HOST ?? initialAppData.USER_API_HOST,
          };
          setCurrentAppData(newAppData);
        })
        .catch((error) => {
          console.error("Error fetching app data:", error);
          // Fallback to initial data if fetch fails
          setCurrentAppData(initialAppData);
        })
        .finally(() => {
          setIsInitialized(true);
          setlastInitializedAt(Date.now());
        }),
    []
  );
  useEffect(() => {
    fetchAppData();
  }, [fetchAppData]);
  useEffect(() => {
    if (lastInitializedAt > 0 && !isInitialized) {
      fetchAppData();
    }
  }, [isInitialized, lastInitializedAt, fetchAppData]);
  const refresh = useCallback(() => {
    setIsInitialized(false);
  }, []);
  const getAppData = useCallback(() => {
    // This function will always return the current appData
    // if appData is invalid, it will trigger a fetch
    const expireMs = expireTime * 1000;
    const elapsed = Date.now() - lastInitializedAt;
    if (lastInitializedAt > 0 && elapsed > expireMs) {
      // If last initialized was more than 3 minutes ago, refresh
      setTimeout(() => {
        refresh();
      }, 0);
    }
    return currentAppData;
  }, [currentAppData, lastInitializedAt, expireTime]);
  return (
    <AppDataContext.Provider
      value={{
        refresh,
        getAppData,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

export default AppDataProvider;
